import {
  Alert,
  Container,
  Divider,
  Paper,
  Snackbar,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import InviteMember from '../../components/Team/InviteMember';
import PendingMemberTable from '../../components/Team/PendingMemberTable';
import TeamMemberTable from '../../components/Team/TeamMemberTable';
import { useUserApi } from '../../services/api/useUser.api';
import { ApiResponse } from '../../types/ApiResponse.type';
import { User } from '../../types/User/User.type.ts';

const TextWrapper = styled('div')({
  gridColumn: '1/5',
});

const ContentWrapper = styled('div')({
  gridColumn: '5/12',
});

function Team() {
  const queryClient = useQueryClient();
  const { getUsers, deactivateUser, getUserInfo, reInviteUser } = useUserApi();

  const { ref: activeUsersRef, inView: activeUsersInView } = useInView();
  const { ref: pendingUsersRef, inView: pendingUsersInView } = useInView();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const { data: user } = useQuery({
    queryKey: ['userInfo'],
    queryFn: getUserInfo,
  });
  const team_id = user?.team?.id;

  const {
    data: infiniteActiveUsers,
    fetchNextPage: fetchActiveUsersNextPage,
    hasNextPage: hasActiveUsersNextPage,
    isFetchingNextPage: isFetchingActiveUsersNextPage,
  } = useInfiniteQuery({
    queryKey: ['activeUsers', team_id],
    queryFn: async ({ pageParam = 1 }) => {
      if (!team_id) {
        throw new Error();
      }
      return await getUsers({ page: pageParam, per_page: 20, team_id, user_status_id: -2 });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: ApiResponse<User[]>) => {
      if ((lastPage?.meta.pagination?.page ?? 0) < (lastPage?.meta.pagination?.pages ?? 0)) {
        return (lastPage?.meta.pagination?.page ?? 0) + 1;
      }
      return undefined;
    },
    enabled: !!team_id,
  });

  const {
    data: infinitePendingUsers,
    fetchNextPage: fetchPendingUsersNextPage,
    hasNextPage: hasPendingUsersNextPage,
    isFetchingNextPage: isFetchingPendingUsersNextPage,
  } = useInfiniteQuery({
    queryKey: ['pendingUsers', team_id],
    queryFn: async ({ pageParam = 1 }) => {
      if (!team_id) {
        throw new Error();
      }
      return await getUsers({ page: pageParam, per_page: 20, team_id, user_status_id: -4 });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: ApiResponse<User[]>) => {
      if ((lastPage?.meta.pagination?.page ?? 0) < (lastPage?.meta.pagination?.pages ?? 0)) {
        return (lastPage?.meta.pagination?.page ?? 0) + 1;
      }
      return undefined;
    },
    enabled: !!team_id,
  });

  const activeUsers = infiniteActiveUsers?.pages?.flatMap((page) => page.data) ?? [];
  const pendingUsers = infinitePendingUsers?.pages?.flatMap((page) => page.data) ?? [];

  const { mutate: removeActiveMember } = useMutation({
    mutationKey: ['removeActiveMember'],
    mutationFn: async (email: string) => {
      return deactivateUser(email);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activeUsers', team_id] });
      queryClient.invalidateQueries({ queryKey: ['pendingUsers', team_id] });
      showSnackbar('Member removed successfully', 'success');
    },
    onError: (error) => {
      showSnackbar(error?.message || 'Failed to remove member. Please try again!', 'error');
    },
  });

  const { mutate: cancelInvitation } = useMutation({
    mutationKey: ['cancelInvitation'],
    mutationFn: async (email: string) => {
      return deactivateUser(email);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activeUsers', team_id] });
      queryClient.invalidateQueries({ queryKey: ['pendingUsers', team_id] });
      showSnackbar('User invitation cancelled successfully', 'success');
    },
    onError: (error) => {
      showSnackbar(error?.message || 'Failed to cancel user invitation. Please try again!', 'error');
    },
  });

  const { mutate: resendInvitation } = useMutation({
    mutationKey: ['resendInvite'],
    mutationFn: async (email: string) => {
      return reInviteUser(email);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activeUsers', team_id] });
      queryClient.invalidateQueries({ queryKey: ['pendingUsers', team_id] });
      showSnackbar('User invitation resent successfully', 'success');
    },
    onError: (error) => {
      showSnackbar(error?.message || 'Failed to resend user invitation. Please try again!', 'error');
    },
  });

  useEffect(() => {
    if (activeUsersInView && fetchActiveUsersNextPage && hasActiveUsersNextPage && !isFetchingActiveUsersNextPage) {
      fetchActiveUsersNextPage();
    }

    if (pendingUsersInView && fetchPendingUsersNextPage && hasPendingUsersNextPage && !isFetchingPendingUsersNextPage) {
      fetchPendingUsersNextPage();
    }
  }, [
    activeUsersInView,
    fetchActiveUsersNextPage,
    isFetchingActiveUsersNextPage,
    hasActiveUsersNextPage,
    pendingUsersInView,
    fetchPendingUsersNextPage,
    isFetchingPendingUsersNextPage,
    hasPendingUsersNextPage,
  ]);

  return (
    <>
      <Container sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr);', gap: 8 }}>
        <TextWrapper>
          <Typography
            variant='h5'
            sx={{
              fontWeight: 'bold',
            }}
          >
            Members
          </Typography>
          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
              marginTop: 2,
            }}
          >
            Enter the email address of the teammate you’d like to invite, adjust user permissions, or remove users as
            needed.
          </Typography>
        </TextWrapper>
        <ContentWrapper>
          <Stack
            component={Paper}
            sx={{
              padding: 3,
              border: '1px solid #cbd5e0',
              boxShadow: 'none',
              borderBottomRightRadius: '0 !important',
              borderBottomLeftRadius: '0 !important',
            }}
          >
            <InviteMember />
          </Stack>
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: 440,
              borderTopLeftRadius: '0 !important',
              borderTopRightRadius: '0 !important',
              border: '1px solid #cbd5e0',
              borderTop: 0,
              boxShadow: 'none',
            }}
          >
            <Table stickyHeader>
              <TableBody>
                {activeUsers.length > 0 ? (
                  activeUsers.map((user: User) => (
                    <TeamMemberTable key={user.id} activeMember={user} handleDeactivateUser={removeActiveMember} />
                  ))
                ) : (
                  <TableRow>
                    <TableCell>
                      <Typography
                        variant='body2'
                        sx={{
                          color: 'text.secondary',
                          textAlign: 'center',
                          padding: 4,
                        }}
                      >
                        No members found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              {hasActiveUsersNextPage && (
                <TableBody>
                  <TableRow ref={activeUsersRef} sx={{ height: '1px', width: '100%' }}></TableRow>
                </TableBody>
              )}
              {isFetchingActiveUsersNextPage && (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', padding: 2 }}>
                      <Typography
                        variant='caption'
                        sx={{
                          color: 'text.secondary',
                          textAlign: 'center',
                        }}
                      >
                        Loading more members...
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </ContentWrapper>
      </Container>
      <Container>
        <Divider sx={{ marginY: 6 }} />
      </Container>
      <Container sx={{ marginBottom: 10, display: 'grid', gridTemplateColumns: 'repeat(12, 1fr);', gap: 8 }}>
        <TextWrapper>
          <Typography
            variant='h5'
            sx={{
              fontWeight: 'bold',
            }}
          >
            Pending invite
          </Typography>
          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
              marginTop: 2,
            }}
          >
            Users that have not yet responded to your invitation
          </Typography>
        </TextWrapper>
        <ContentWrapper>
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: 440,
              borderRadius: 2,
              border: '1px solid #cbd5e0',
              boxShadow: 'none',
            }}
          >
            <Table stickyHeader>
              <TableBody>
                {pendingUsers.length > 0 ? (
                  pendingUsers.map((user: User) => (
                    <PendingMemberTable
                      key={user.id}
                      pendingMember={user}
                      handleDeactivateUser={cancelInvitation}
                      handleResendInvite={resendInvitation}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell>
                      <Typography
                        variant='body2'
                        sx={{
                          color: 'text.secondary',
                          textAlign: 'center',
                          padding: 4,
                        }}
                      >
                        No pending members found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              {hasPendingUsersNextPage && (
                <TableBody>
                  <TableRow ref={pendingUsersRef} sx={{ height: '1px', width: '100%' }}></TableRow>
                </TableBody>
              )}
              {isFetchingPendingUsersNextPage && (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', padding: 2 }}>
                      <Typography
                        variant='caption'
                        sx={{
                          color: 'text.secondary',
                          textAlign: 'center',
                        }}
                      >
                        Loading more pending members...
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </ContentWrapper>
      </Container>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default Team;
